import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiClient } from '../api/apiClient';
import { InvoicePortalResponse } from '../../entities/InvoicePortal';

// Configurar el cliente API
const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/';
const apiClient = new ApiClient({
  baseURL: baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL,
});

interface UseInvoicePortalResult {
  data: InvoicePortalResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useInvoicePortal = (
  shortUuid: string | undefined
): UseInvoicePortalResult => {
  const [data, setData] = useState<InvoicePortalResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  const currentUuid = useRef<string | undefined>(undefined);

  const fetchInvoicePortal = useCallback(async () => {
    if (!shortUuid) {
      setError('UUID es requerido');
      setIsLoading(false);
      return;
    }

    // Evitar llamadas duplicadas para el mismo UUID
    if (hasFetched.current && currentUuid.current === shortUuid) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      hasFetched.current = true;
      currentUuid.current = shortUuid;

      const response = await apiClient.get<InvoicePortalResponse>(
        `/billing/portal/${shortUuid}/invoices/`
      );

      setData(response.data);
    } catch (err: any) {
      console.error('Error fetching invoice portal data:', err);

      if (err.status === 404) {
        setError('Portal no encontrado');
      } else if (err.status === 410) {
        setError('Portal ya no está disponible');
      } else {
        setError(err.message || 'Error al cargar los datos del portal');
      }
    } finally {
      setIsLoading(false);
    }
  }, [shortUuid]);

  useEffect(() => {
    // Reset cuando cambia el UUID
    if (currentUuid.current !== shortUuid) {
      hasFetched.current = false;
      currentUuid.current = shortUuid;
    }

    if (shortUuid && !hasFetched.current) {
      fetchInvoicePortal();
    }
  }, [shortUuid, fetchInvoicePortal]);

  const refetch = useCallback(() => {
    hasFetched.current = false; // Permite refetch manual
    fetchInvoicePortal();
  }, [fetchInvoicePortal]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
